import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';

const GridBg = () => (
  <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
);

export function DiagnosticPreview() {
  return (
    <div className="bg-zinc-950">
      <SEOHead />
      <section className="relative section-border pt-10 pb-6 md:pt-12 md:pb-8 overflow-hidden">
        <GridBg />
        <div className="container-site relative z-10 max-w-4xl">
          <div className="border-b border-zinc-800 pb-12 mb-12">
            <span className="text-xs font-mono tracking-widest uppercase text-amber-500 block mb-5">Output</span>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-zinc-100 leading-tight">Diagnostic Preview</h1>
            <p className="mt-6 text-base text-zinc-400 max-w-xl leading-relaxed">
              The executive summary below demonstrates the structure of the OnPoint Diagnostics deliverable set, and the format. Content is illustrative.
            </p>
          </div>

          {/* Memo structure */}
          <div className="border border-zinc-800 rounded-md overflow-hidden mb-8">
            <div className="bg-zinc-900 border-b border-zinc-800 px-8 py-5 flex items-center justify-between">
              <div>
                <span className="text-xs font-mono tracking-widest uppercase text-zinc-600">Memo</span>
                <h2 className="font-heading text-lg font-medium text-zinc-100 mt-1">Executive Summary</h2>
              </div>
              <span className="text-xs font-mono text-amber-500/70 bg-amber-500/5 border border-amber-500/10 px-2 py-0.5 rounded">
                Illustrative Artifact
              </span>
            </div>
            <div className="p-8 bg-zinc-950 font-mono text-xs text-zinc-400 space-y-6">
              <div className="grid grid-cols-2 gap-4 pb-5 border-b border-zinc-800">
                <div><div className="text-zinc-600 mb-1">TO:</div><div className="text-zinc-300">[Delivery Email]</div></div>
                <div><div className="text-zinc-600 mb-1">FROM:</div><div className="text-zinc-300">OnPoint.Works | WFM Analytics Lab</div></div>
                <div><div className="text-zinc-600 mb-1">DATE:</div><div className="text-zinc-300">[Delivery Date]</div></div>
                <div><div className="text-zinc-600 mb-1">RE:</div><div className="text-zinc-300">OnPoint Diagnostics — Executive Summary</div></div>
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-wider text-[10px] mb-3">I. PRIMARY DISTORTION DOMAIN</div>
                <div className="pl-4 border-l border-zinc-800 space-y-2">
                  <p><span className="text-amber-500">Domain Classification: </span>Forecast Decomposition Failure (Confidence: 72)</p>
                  <p className="text-zinc-500 leading-relaxed">-Systematic decomposition error in the pattern component continues to generate a lasting interval bias focused over the operating windows of 09:00 - 11:30 and 14:00 - 15:30. This error has a non-random structure with weekly recurrence and a coefficient of variation below the random threshold...</p>
                </div>
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-wider text-[10px] mb-3">II. SECONDARY INTERACTION DRIVERS</div>
                <div className="pl-4 border-l border-zinc-800 space-y-2">
                  {['-Schedule Architecture Distortion (Confidence: 58): Mismatch of shift boundary to primary domain exposure intervals amplifies error impact and reduces natural correction mechanisms.',
                    '-Intraday Signal Collapse (Confidence 44): Reforecast has tended to become inaccurate after 10:30 and so has not been effective at correcting the forecast.'].map((d) => (
                    <p key={d} className="text-zinc-500 leading-relaxed">{d}</p>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-wider text-[10px] mb-3">III. SIGNAL STRUCTURE AND EXPOSURE MAPPING</div>
                <div className="pl-4 border-l border-zinc-800">
                  <p className="text-zinc-500 leading-relaxed">-Recurrence frequency 4.2x / week. Sensitivity Index 0.71 (high). Main domain signal can be identified in (X) of (N) weeks analysed. Refer to Annex B, FIG 1.0 for interval intensity map...</p>
                </div>
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-wider text-[10px] mb-3">IV. BOUNDED NEXT-STEP OPTIONS</div>
                <div className="pl-4 border-l border-zinc-800 space-y-1">
                  {['-Option A: Extended forecast decomposition (Module A) is recommended if pattern separation analysis needs to be performed.',
                    '-Option B: Accept current classification and direct internal remediation effort work focused on the forecast architecture.',
                    '-Option C: No further action — classification completed, findings documented.'].map((opt) => (
                    <p key={opt} className="text-zinc-500 leading-relaxed">{opt}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Annexes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {[
              { ref: 'Annex A', label: 'Interval Analysis Tables', desc: 'Tabular domain scores by queue and interval window.' },
              { ref: 'Annex B', label: 'Signal Structure Charts', desc: 'Heatmaps, residual lines, and exposure maps.' },
              { ref: 'Annex C', label: 'Data Sufficiency', desc: 'Assessment and next engagement export specification.' },
            ].map((a) => (
              <div key={a.ref} className="border border-zinc-800 rounded-md p-5 bg-zinc-900/20">
                <span className="text-xs font-mono text-amber-500">{a.ref}</span>
                <div className="text-sm font-medium text-zinc-200 mt-1.5 mb-1">{a.label}</div>
                <div className="text-xs text-zinc-500">{a.desc}</div>
              </div>
            ))}
          </div>

          <div className="flex gap-4">
            <Link to="/intake" data-testid="diagnostic-preview-intake-cta" className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-100 text-zinc-900 rounded-md text-sm font-medium hover:bg-white transition-colors duration-200">
              Begin Intake <ArrowRight size={14} />
            </Link>
            <Link to="/sample-memo" className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 rounded-md text-sm hover:border-zinc-500 hover:text-white transition-colors duration-200">
              Full Sample Memo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
